import { motion } from 'framer-motion';

interface ProgressBarProps {
  /**
   * Progress percentage (0-100)
   */
  progress: number;
  /**
   * Whether to animate the progress bar
   */
  animated?: boolean;
  /**
   * Animation duration in seconds
   */
  animationDuration?: number;
  /**
   * Animation delay in seconds
   */
  animationDelay?: number;
  /**
   * Custom CSS classes for the container
   */
  className?: string;
  /**
   * Custom CSS classes for the progress bar
   */
  progressClassName?: string;
  /**
   * Height of the progress bar
   */
  height?: string;
  /**
   * Whether to show rounded corners
   */
  rounded?: boolean;
}

export function ProgressBar({
  progress,
  animated = true,
  animationDuration = 1.2,
  animationDelay = 0.5,
  className = '',
  progressClassName = '',
  height = 'h-4',
  rounded = true,
}: ProgressBarProps) {
  const progressWidth = `${Math.min(Math.max(progress, 0), 100)}%`;

  const progressBarStyle = {
    background: 'var(--clinic-secondary)',
  };

  const containerClasses = `w-full bg-gray-200 ${height} ${
    rounded ? 'rounded-full' : ''
  } overflow-hidden ${className}`;

  const progressClasses = `${height} ${
    rounded ? 'rounded-full' : ''
  } ${progressClassName}`;

  if (animated) {
    return (
      <div className={containerClasses}>
        <motion.div
          className={progressClasses}
          style={progressBarStyle}
          initial={{ width: '0%' }}
          animate={{ width: progressWidth }}
          transition={{
            duration: animationDuration,
            ease: 'easeOut',
            delay: animationDelay,
          }}
        />
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div
        className={progressClasses}
        style={{
          ...progressBarStyle,
          width: progressWidth,
        }}
      />
    </div>
  );
}
